import React__default, { forwardRef, useEffect, useContext } from 'react';
import classNames from 'classnames';
import { useDraggable } from '@dnd-kit/core';
import { isSheetExpanded } from '../../utilities.mjs';
import styles from './DraggableRegion.scss.mjs';
import { useUniqueId } from '../../../../utilities/unique-id/hooks.mjs';
import { useCompoundComponentProviderContext } from '../CompoundComponents/components/CompoundComponentProvider/CompoundComponentProvider.mjs';
import { TransitionContext } from '../../../TransitionGroupManager/TransitionGroupManager.mjs';
import { Header } from '../Header/Header.mjs';
import { TransitionContainer } from '../../../TransitionContainer/TransitionContainer.mjs';

const DraggableRegion = /*#__PURE__*/forwardRef(function DraggableRegion({
  children,
  expansion,
  fullCollapse,
  isModal,
  backgroundSheetContent,
  showBackgroundSheet,
  minHeightCollapsed,
  maxHeight,
  scrollable,
  onTitleTap,
  ...headerProps
}, ref) {
  var _transform$y;
  const {
    isDragging,
    listeners,
    transform,
    setNodeRef: setDragHandleNodeRef
  } = useDraggable({
    id: useUniqueId('BottomSheetDraggableRegion')
  });
  const context = useCompoundComponentProviderContext();
  const expanded = isSheetExpanded(expansion);
  const [componentMounted, setComponentMounted] = React__default.useState(false);
  useEffect(() => {
    setComponentMounted(true);
    return () => {
      setComponentMounted(false);
    };
  }, []);
  const {
    noVisibleHeaderContent
  } = context;
  const transitionContext = useContext(TransitionContext);
  const draggableRegionClassNames = classNames(styles.DraggableRegion, {
    [styles.dragging]: isDragging,
    [styles.expanded]: expanded && componentMounted,
    [styles.fullCollapse]: fullCollapse,
    [styles.isModal]: isModal,
    [styles.hasBackgroundSheet]: backgroundSheetContent,
    [styles.showBackgroundSheet]: showBackgroundSheet && !isDragging && !expanded
  });
  const dragHandleClassNames = classNames(styles.DragHandle, {
    [styles.reducedHeightDragHandle]: noVisibleHeaderContent
  });
  const {
    sheetHeight,
    translateHeight
  } = calcCssHeightValues(maxHeight, expansion, isDragging);
  const minHeightCollapsedStyles = noVisibleHeaderContent && minHeightCollapsed != null ? {
    '--osui_height-bottom-sheet-collapsed-drag-handle': `${maxHeight * minHeightCollapsed * 100}vh`,
    '--osui_height-bottom-sheet-header': `${maxHeight * minHeightCollapsed * 100}vh`
  } : undefined;
  const draggableRegionStyles = {
    '--osui_translate-y-bottom-sheet-draggable-region': `${(_transform$y = transform === null || transform === void 0 ? void 0 : transform.y) !== null && _transform$y !== void 0 ? _transform$y : 0}px`,
    '--osui_content-bottom-sheet-background-sheet-content': `${backgroundSheetContent ? JSON.stringify(backgroundSheetContent) : null}`,
    '--osui_max-height-bottom-sheet': `${sheetHeight * 100}vh`,
    '--osui_translate-height-bottom-sheet': `${translateHeight * 100}vh`,
    ...minHeightCollapsedStyles
  };
  const transitionClasses = {
    exit: classNames(styles.exiting)
  };
  const scrollableAttributes = scrollable ? {
    'data-polaris-scrollable': 'true'
  } : {};
  const markup = /*#__PURE__*/React__default.createElement("div", {
    className: draggableRegionClassNames,
    style: draggableRegionStyles,
    ref: ref
  }, /*#__PURE__*/React__default.createElement("div", Object.assign({
    className: dragHandleClassNames
  }, listeners, {
    ref: setDragHandleNodeRef
  }), /*#__PURE__*/React__default.createElement(Header, Object.assign({
    expanded: expanded,
    onTap: onTitleTap
  }, headerProps))), /*#__PURE__*/React__default.createElement("div", Object.assign({
    className: classNames(styles.Content, scrollable && styles.scrollable)
  }, scrollableAttributes), /*#__PURE__*/React__default.createElement("div", {
    className: styles.ContentInterior
  }, children)));
  return transitionContext ? /*#__PURE__*/React__default.createElement(TransitionContainer, {
    transitionClassNames: transitionClasses
  }, markup) : markup;
});
function calcCssHeightValues(maxHeight, expansionState, isDragging) {
  const midHeight = maxHeight * 2 / 3;
  const isHalfExpanded = expansionState === 'mid';
  const getSheetHeight = () => {
    if (isHalfExpanded) {
      return isDragging ? maxHeight : midHeight;
    }
    return maxHeight;
  };
  return {
    sheetHeight: getSheetHeight(),
    translateHeight: isHalfExpanded ? midHeight : maxHeight
  };
}

export { DraggableRegion, calcCssHeightValues };
